import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';

type Props = { value: string; onChange: (value: string) => void; label: string; placeholder: string; compact?: boolean };

/** Typing is local and debounced; the URL (via onChange) stays the source of truth. */
export function SearchBox({ value, onChange, label, placeholder, compact = false }: Props) {
  const [draft, setDraft] = useState(value);
  const [synced, setSynced] = useState(value);
  const debounced = useDebouncedValue(draft);
  const onChangeRef = useRef(onChange);

  // Back/forward or "Clear filters" changed the URL: adopt it (React's derived-state pattern).
  if (value !== synced) {
    setSynced(value);
    setDraft(value);
  }

  useEffect(() => { onChangeRef.current = onChange; });
  // Waits for the debounce to catch up with the box: right after a clear, `debounced` still holds the old text and would restore it.
  useEffect(() => {
    const next = debounced.trim();
    if (debounced === draft && next !== synced) onChangeRef.current(next);
  }, [debounced, draft, synced]);

  return (
    <TextField
      fullWidth
      type="search"
      placeholder={placeholder}
      value={draft}
      onChange={(e) => { setDraft(e.target.value); }}
      slotProps={{
        htmlInput: { 'aria-label': label },
        input: { startAdornment: <InputAdornment position="start"><Search size={16} aria-hidden /></InputAdornment> },
      }}
      sx={{ '& .MuiOutlinedInput-root': { minHeight: compact ? { xs: 44, md: 34 } : 44, fontSize: '0.875rem', borderRadius: 1.5 } }}
    />
  );
}
